import React, { useState, useEffect } from 'react'
import WeatherDisplay from './components/Views/WeatherDisplay/WeatherDisplay'
import WeatherError from './components/Views/WeatherError/WeatherError'
import WeatherLoading from './components/Views/WeatherLoading/WeatherLoading'

import { settings, textLangMap } from './settings/config'
import { GeoLocation, IWeatherState } from './types'
import { getApiLocation, getApiWeather, getApiWeatherDaily } from './api'
import {
  getCurrentPosition,
  setBackgroundClass,
  validateLocation,
  validateWeather,
  validateWeatherDaily
} from './utils'

const lang = document.documentElement.lang
const app_theme = document.documentElement.getAttribute('data-mp-app-theme')
const texts = textLangMap(lang)

const App = () => {
  const [state, setState] = useState<IWeatherState>({
    loadingStatus: 'loading',
    weatherData: null,
    locationData: null,
    source: null
  })

  const getCurrentWeather = async (geolocation: GeoLocation) => {
    try {
      let weatherDaily
      const location = await getApiLocation(geolocation)
      const weather = await getApiWeather(location.source.locationId)

      if (app_theme === 'moment') {
        weatherDaily = await getApiWeatherDaily(location.source.locationId)
      }

      return { location, weather, weatherDaily }
    } catch (e) {
      console.error(`Error getting data from Location API`, e)
      setState(prevState => ({ ...prevState, loadingStatus: 'error' }))
      return null
    }
  }

  useEffect(() => {
    const geolocation = getCurrentPosition()
    getCurrentWeather(geolocation)
      .then(res => {
        const { weather, location, weatherDaily } = res
        if (
          validateLocation(location) &&
          validateWeather(weather) &&
          validateWeatherDaily(weatherDaily, app_theme)
        ) {
          setState(previousState => ({
            ...previousState,
            weatherData: {
              ...weather,
              daily: weatherDaily
            },
            locationData: location.location,
            loading: false,
            source: location.source,
            loadingStatus: 'loaded'
          }))
        } else {
          throw new Error('Weather widget invalid data')
        }
      })
      .catch(_ => {
        setState(prevState => ({ ...prevState, loadingStatus: 'error' }))
      })
  }, [])

  const displayProps = {
    weatherData: state.weatherData,
    locationData: state.locationData,
    iconPath: settings.icon_path,
    source: state.source,
    texts
  }

  const selectDisplayView = () => {
    switch (state.loadingStatus) {
      case 'loaded':
        return <WeatherDisplay {...displayProps} />
      case 'error':
        return <WeatherError {...texts.error} />
      case 'loading':
      default:
        return <WeatherLoading loadingText={texts.loading} />
    }
  }

  return (
    <div className={`${setBackgroundClass()} ${state.loadingStatus}`}>
      <span className="title">Weather Forecast</span>
      {selectDisplayView()}
    </div>
  )
}

export default App
